import { Hammer, TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { HoverCardContent } from '@/components/ui/hover-card'
import { formatTimelineTimestamp } from './timelineMath'
import { RaidPlanCast } from 'app/frontend/types/custom/RaidPlan'
import { ResolvedAssignmentEntity } from '../../TeamEvent/components/eventActivityTimelineResolver'
import { ResolvedAssignmentParticipant } from '../../TeamEvent/components/eventActivityTimelineResolver'
import { EncounterAssignmentEntityOverride } from '../../TeamEvent/components/eventActivityPlanOverrides'
import AssignmentEntityChip from './AssignmentEntityChip'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import EventPlanOverridePopover from '../../TeamEvent/components/EventPlanOverridePopover'
import { defaultAggregateOptions } from '../../TeamEvent/components/EventPlanOverridePopover'
import { renderResolvedAssignmentEntityLabel } from '../../TeamEvent/components/renderResolvedAssignmentEntityLabel'
import { useAuth } from 'app/frontend/base/config/context/auth'

type Props = {
  cast: RaidPlanCast
  abilityName: string
  resolvedTargets: ResolvedAssignmentEntity[]
  selectedParticipants: ResolvedAssignmentParticipant[]
  canEditCastTargets: boolean
  onCastTargetOverrideSelect?: (selection: {
    castId: string
    entityId: string
    override: EncounterAssignmentEntityOverride
  }) => void
}

const aggregateOptions: Extract<
  EncounterAssignmentEntityOverride,
  { kind: 'aggregate' }
>[] = defaultAggregateOptions.map((option) => ({
  kind: 'aggregate',
  entity: option.entity,
}))

const CastTargetsHoverCardContent = ({
  cast,
  abilityName,
  resolvedTargets,
  selectedParticipants,
  canEditCastTargets,
  onCastTargetOverrideSelect,
}: Props) => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const message = cast.message?.trim() ?? ''
  const targetGroups = cast.targets ?? []
  const participantClassByCharacterId = Object.fromEntries(
    selectedParticipants.map((participant) => [
      participant.characterId,
      participant.characterClass,
    ]),
  )
  const targetRows = targetGroups.flatMap((group, groupIndex) =>
    group.map((entity, entityIndex) => ({
      entityIndex,
      entityId: `${groupIndex}:${entityIndex}`,
      sourceEntity: entity,
    })),
  )

  return (
    <HoverCardContent
      side='top'
      align='center'
      className='w-fit px-3 py-1.5 text-center'
    >
      <div className='text-sm font-medium'>{abilityName}</div>
      {message.length ? (
        <div className='text-xs text-muted-foreground whitespace-pre-line'>
          {message}
        </div>
      ) : null}
      <div className='text-xs text-muted-foreground'>
        {formatTimelineTimestamp(cast.at)}
      </div>

      <div className='flex flex-wrap items-center gap-1 mt-2 max-w-[20rem] justify-center'>
        {targetRows.map((target, flatIndex) => {
          const resolved = resolvedTargets[flatIndex]
          const label =
            resolved === undefined
              ? getAssignmentEntityLabel(t, target.sourceEntity)
              : renderResolvedAssignmentEntityLabel({
                  t,
                  entity: resolved,
                  participantClassByCharacterId,
                })
          const chip = (
            <AssignmentEntityChip
              index={target.entityIndex}
              label={label}
              warning={Boolean(resolved?.issue)}
              endSlot={
                resolved?.issue ? (
                  <TriangleAlert className='h-3.5 w-3.5 text-warning-foreground' />
                ) : resolved?.isOverridden ? (
                  <Hammer className='h-3.5 w-3.5 text-info-foreground' />
                ) : undefined
              }
            />
          )
          let selectedOverride: EncounterAssignmentEntityOverride | undefined
          if (resolved?.kind === 'participant') {
            selectedOverride = {
              kind: 'participant',
              characterId: resolved.participant.characterId,
            }
          } else if (
            resolved?.kind === 'aggregate' &&
            (resolved.sourceEntity.kind === 'everyone' ||
              resolved.sourceEntity.kind === 'collection' ||
              resolved.sourceEntity.kind === 'group')
          ) {
            selectedOverride = {
              kind: 'aggregate',
              entity: resolved.sourceEntity,
            }
          }
          if (!canEditCastTargets || !onCastTargetOverrideSelect) {
            return <span key={target.entityId}>{chip}</span>
          }
          return (
            <EventPlanOverridePopover
              key={target.entityId}
              trigger={chip}
              disabled={accessLevel !== 'admin'}
              participants={selectedParticipants}
              aggregateOptions={aggregateOptions}
              selectedOverride={selectedOverride}
              onSelect={(override) =>
                onCastTargetOverrideSelect({
                  castId: cast.id,
                  entityId: target.entityId,
                  override,
                })
              }
            />
          )
        })}
      </div>
    </HoverCardContent>
  )
}

export default CastTargetsHoverCardContent
